import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useRef, useState } from 'react'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

import { Table } from '@/components/shadcn/table'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { Button } from '@/components/shadcn/button'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import { JsonFormSchemaV2 } from '../FormComponent/types'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { SearchInput } from '../shared/SearchInput'
import { BulkAction, TableAction, TableConfig, TableGroupConfig } from './types-v4'
import { CompactViewToggle } from './CompactViewToggle'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
// import { getComponentHeights } from '../utils'
import { useColumnWidths } from './hooks/useColumnWidths'
import { useFrozenColumns } from './hooks/useFrozenColumns'
import { useSelectionEnabled } from './hooks/useSelectionEnabled'
import { useTableColumns } from './hooks/useTableColumns'
import { DataTableHeader } from './components/DataTableHeader'
import { DataTableBody } from './components/DataTableBody'
import { TableActions } from './components/TableActions'
import { BulkActions } from './components/BulkActions'
import { useTableDataGrouping } from './hooks/useTableDataGrouping'
import { useExpanderColumn } from './hooks/useExpanderColumn'
import { useTableRegrouping } from './hooks/useTableRegrouping'
import { useTableDragDrop } from './hooks/useTableDragDrop'
import { TableDragOverlay } from './components/TableDragOverlay'
import { GroupingActions } from './components/GroupingActions'
import { IconV2RotateClockwiseOutline } from '@icons/IconV2RotateClockwiseOutline'

// Threshold for enabling virtualization - only virtualize if we have more than this many rows
const VIRTUALIZATION_THRESHOLD = 100

interface TableContentProps<TData, TValue> {
  tableConfig: TableConfig
  columns: ColumnDef<TData, TValue>[]
  actions?: TableAction[]
  bulkActions?: BulkAction[]
  data: TData[]
  // groups?: Record<string, any>[]
  groups?: TData[]
  groupConfig?: TableGroupConfig
  formSchema?: JsonFormSchemaV2
  createSubmitHandlerId?: string
  tileFilters?: React.ReactNode
  reloadHandler: () => void
  isRefetching: boolean
}

export function TableContent<TData, TValue>({
  tableConfig,
  columns,
  actions,
  bulkActions,
  data,
  groups,
  groupConfig,
  formSchema,
  createSubmitHandlerId,
  tileFilters,
  reloadHandler,
  isRefetching,
}: TableContentProps<TData, TValue>) {
  // Create a reference to the table body
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const {
    title,
    description,
    canRefresh = true,
    canSearch = true,
    emphasizeHeader,
    compactMode: defaultCompactMode,
    freezePane,
  } = tableConfig
  // const { contentHeight } = getComponentHeights(emphasizeHeader)
  const [globalFilter, setGlobalFilter] = useState('')
  const headerMeasureRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const cellMeasureRefs = useRef<Record<string, HTMLDivElement[]>>({})
  const overlayCellMeasureRefs = useRef<Record<string, HTMLDivElement[]>>({})
  const [compactMode, setCompactMode] = useState(() => defaultCompactMode)
  const [isRegrouping, setIsRegrouping] = useState(false)

  // Determine default expansion state from groupConfig
  const defaultExpanded = groupConfig?.defaultExpanded ?? true

  const tableRef = useRef<ReturnType<typeof useReactTable<TData>> | null>(null)

  const nrOfColumns = columns.length

  const handlerId = createSubmitHandlerId || ''
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm, isPending: isSubmitting } = useSubmitForm(handlerId, tileFilterSelections, 'create')

  // Check if selection is enabled
  const isSelectionEnabled = useSelectionEnabled(actions, bulkActions)

  // Transform data for grouping if config is present
  const groupedData = useTableDataGrouping({
    data,
    groups,
    groupConfig,
  })

  // Manage regrouping logic (data, expansion, dropping)
  const {
    localData,
    expandedState,
    handleExpandedChange,
    handleUngroup,
    handleDrop,
    handleUpdateGroup,
    handleDeleteGroup,
    handleResetGroups,
  } = useTableRegrouping({
    groupedData,
    defaultExpanded,
    tableRef,
  })

  // Manage drag and drop sensors and events
  const { activeId, sensors, handleDragStart, handleDragEnd, handleDragCancel } = useTableDragDrop({
    handleDrop,
  })

  // Prepare columns including selection column if enabled
  const columnsWithSelection = useTableColumns(columns, isSelectionEnabled)

  // Add expander column if grouping is enabled
  const finalColumns = useExpanderColumn({
    columns: columnsWithSelection,
    isGroupingEnabled: !!groupConfig,
    isRegrouping,
    onUngroup: handleUngroup,
    onDeleteGroup: handleDeleteGroup,
  })

  // Build a safe initial sorting config only if the column exists
  const columnIds: string[] = columns
    .map((c) => {
      const def = c as ColumnDef<TData, TValue> & { accessorKey?: string }
      return (def.id as string) || (def.accessorKey as string) || ''
    })
    .filter(Boolean)

  const initialSorting =
    tableConfig.defaultSort && columnIds.includes(tableConfig.defaultSort.column)
      ? [
          {
            id: tableConfig.defaultSort.column,
            desc: tableConfig.defaultSort.direction === 'desc',
          },
        ]
      : []

  const table = useReactTable({
    data: localData,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getSubRows: (row: any) => {
      if (row && row.subRows && row.subRows.length > 0) {
        return row.subRows
      }
      return undefined
    },
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: handleExpandedChange,
    globalFilterFn: 'includesString',
    enableRowSelection: isSelectionEnabled,
    state: {
      globalFilter,
      expanded: Object.keys(expandedState).length > 0 ? expandedState : defaultExpanded ? true : {},
    },
    initialState: {
      sorting: initialSorting,
      expanded: defaultExpanded ? true : {}, // Default to expanded if needed or controllable
    },
    meta: {
      isRegrouping,
      updateGroup: handleUpdateGroup,
      deleteGroup: handleDeleteGroup,
    },
  })

  // Store table reference for use in callbacks
  tableRef.current = table

  // Get filtered rows
  const { rows } = table.getRowModel()
  const shouldVirtualize = rows.length > VIRTUALIZATION_THRESHOLD

  // Set up the virtualizer only if we need it
  const rowVirtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: rows.length,
    getScrollElement: () => scrollAreaRef.current as HTMLDivElement,
    estimateSize: () => (compactMode ? 50 : 78), // Estimate row height (adjust based on your actual row height)
    overscan: 5, // Number of items to render before/after the visible area
    enabled: shouldVirtualize,
  })

  // Compute widths for columns
  const computedColWidths = useColumnWidths(table, headerMeasureRefs, cellMeasureRefs)

  // Calculate sticky offsets for frozen columns
  // Frozen count = (Selection ? 1 : 0) + (Expander/Grouping ? 1 : 0) + (Configured FreezePane ? N : 0)
  const frozenColumnCount = (isSelectionEnabled ? 1 : 0) + (groupConfig ? 1 : 0) + (freezePane ?? 0)
  const { leafColumns, frozenColOffsetsById } = useFrozenColumns(table, frozenColumnCount, computedColWidths)
  const canRegroup = groupConfig?.canRegroup ?? false

  const { mutate: submitGroups, isPending: isSubmittingGroups } = useSubmitForm(
    groupConfig?.handlerId || '',
    tileFilterSelections
  )

  const onToggleRegrouping = useCallback(() => {
    setIsRegrouping((prev) => !prev)
  }, [])

  const { groupIdCollection = 'dataIds' } = groupConfig || {}
  const onSaveGroups = useCallback(() => {
    const groups = localData
      .filter((item) => item && typeof item === 'object' && 'isGroup' in item && item.isGroup && 'subRows' in item)
      .map((item) => {
        const { subRows, isGroup, ...rest } = item as { subRows: TData[]; isGroup: boolean; [key: string]: unknown }
        return { ...rest, [groupIdCollection]: subRows.map((row) => (row as { id: string | number }).id) }
      })
    submitGroups({ groups })
    setIsRegrouping(false)
  }, [localData, submitGroups, groupIdCollection])

  const onCancelGroups = useCallback(() => {
    handleResetGroups()
    setIsRegrouping(false)
  }, [handleResetGroups])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToVerticalAxis]}
    >
      <div className="flex h-full flex-col">
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            <GroupingActions
              isRegrouping={isRegrouping}
              canRegroup={canRegroup}
              onToggleRegrouping={onToggleRegrouping}
              onSaveGroups={onSaveGroups}
              onCancelGroups={onCancelGroups}
              isSubmittingGroups={isSubmittingGroups}
            />
            <BulkActions actions={bulkActions} table={table} data={data} title={title} />
            {canRefresh && !isRegrouping && (
              <TooltipButton tooltip="Refresh data">
                <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={reloadHandler}>
                  <IconV2RotateClockwiseOutline className="size-6" />
                </Button>
              </TooltipButton>
            )}
            {canSearch && <SearchInput value={globalFilter} onChange={setGlobalFilter} />}
            {tileFilters}
            <div className="space-x-1">
              {!isRegrouping && (
                <TableActions
                  actions={actions}
                  formSchema={formSchema}
                  submitForm={submitForm}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </ComponentActionsContainer>
        </ComponentHeader>
        {/* <div style={{ height: contentHeight }} className="relative w-full pt-5"> */}
        <div className="relative min-h-0 w-full flex-1 pt-5">
          <CompactViewToggle compactMode={compactMode} setCompactMode={setCompactMode} />
          <div
            ref={scrollAreaRef}
            className="h-auto max-h-full w-full overflow-auto rounded-md border [&>div]:overflow-visible"
          >
            <Table className="min-w-max table-auto border-separate border-spacing-0">
              <colgroup>
                {table.getAllLeafColumns().map((column) => (
                  <col
                    key={column.id}
                    style={{
                      minWidth: computedColWidths[column.id] ? `${computedColWidths[column.id]}px` : undefined,
                      width: column.columnDef.size ? `${column.columnDef.size}px` : undefined,
                    }}
                  />
                ))}
              </colgroup>
              <DataTableHeader
                table={table}
                frozenColOffsetsById={frozenColOffsetsById}
                leafColumns={leafColumns}
                freezePane={frozenColumnCount}
                headerMeasureRefs={headerMeasureRefs}
              />
              <DataTableBody
                tableBodyRef={tableBodyRef}
                isRefetching={isRefetching}
                nrOfColumns={nrOfColumns}
                rows={rows}
                shouldVirtualize={shouldVirtualize}
                rowVirtualizer={rowVirtualizer}
                frozenColOffsetsById={frozenColOffsetsById}
                leafColumns={leafColumns}
                freezePane={frozenColumnCount}
                compactMode={compactMode}
                cellMeasureRefs={cellMeasureRefs}
                isRegrouping={isRegrouping}
                activeDragId={activeId}
              />
            </Table>
          </div>
        </div>
      </div>
      <TableDragOverlay
        activeId={activeId}
        table={table}
        computedColWidths={computedColWidths}
        frozenColOffsetsById={frozenColOffsetsById}
        leafColumns={leafColumns}
        frozenColumnCount={frozenColumnCount}
        compactMode={compactMode}
        isRegrouping={isRegrouping}
        cellMeasureRefs={overlayCellMeasureRefs}
      />
    </DndContext>
  )
}
